import React from 'react';
import { useAppSelector } from '../../../../../app/hooks';
import {
    LEADS_VALUE_GETTERS,
    buildLeadModalActionConfig,
    resolveLeadDisplayName,
    useLeadDynamicDropdownOptions,
    useLeadDrawerCellRenderers,
    useLeadFieldCellDisabled,
    useLeadFieldUpdaters,
    useLeadHistoryDrawer,
    useLeadsColumnsConfig,
} from '../../../../Investor/Portfolio/components/CompaniesList/useLeadEditing';
import HistoryDrawer from '../../../../../common/HistoryDrawer/HistoryDrawer';
import ViewAllPropertiesDrawer from '../../../../../common/ViewAllProperties/ViewAllPropertiesDrawer';
import { buildPropertyColumns } from '../../../../../common/ViewAllProperties/buildPropertyColumns';
import { USER_TYPE } from '../../../../../utils/constants/user';
import { VISIBILITY_KEYS } from '../../../../../utils/constants/visibilityKeys';
import { useFeatureVisibility } from '../../../../../utils/useVisibilityConfig';
import { useCompanyLeadProperties } from './useCompanyLeadProperties';

type Props = {
    assesseeOrgId: string | undefined;
    companyAccessType: string | undefined;
    onClose: () => void;
};

// The history drawer is a sibling after the SideDrawer (not inside its animated body, which would clip it).
const CompanyLeadPropertiesDrawer: React.FC<Props> = ({ assesseeOrgId, companyAccessType, onClose }) => {
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const partnerData = useAppSelector((state) => state.investor.partnerData);
    const isPartner = userData?.accountType === USER_TYPE.PARTNER;

    const { leadsColumnsConfig } = useLeadsColumnsConfig({
        investorOrgId: platformData?.investorOrgId,
        accountType: userData?.accountType,
        primaryEmail: userData?.primaryEmail,
    });

    const { partnerNameDropdownOptions, podUserDropdownOptions, getStatusDropdownList } = useLeadDynamicDropdownOptions({
        partnerData,
        owners,
    });
    const updaters = useLeadFieldUpdaters({ owners, userName: userData?.userName });

    const getLeadsFieldCellDisabled = useLeadFieldCellDisabled();

    const leadEditableCellRenderers = useLeadDrawerCellRenderers({
        isPartner,
        getLeadsFieldCellDisabled,
        updaters,
        getStatusDropdownList,
    });

    const { historyDrawerState, handleOwnerHistoryClick, handleHistoryDrawerClose } = useLeadHistoryDrawer({
        partnerNameDropdownOptions,
        podUserDropdownOptions,
    });

    const handleDataPendingClick = () => undefined;
    const modalActionConfig = buildLeadModalActionConfig({ handleDataPendingClick, accountType: userData?.accountType });

    const { leadRow, loading, error, notFound } = useCompanyLeadProperties({
        assesseeOrgId,
        companyAccessType,
        investorOrgId: platformData?.investorOrgId,
    });

    const { dataPendingEnabledVisibility } = useFeatureVisibility({ dataPendingEnabledVisibility: VISIBILITY_KEYS.DATA_PENDING.ENABLED });
    const columns = buildPropertyColumns(leadsColumnsConfig?.columns, { dataPendingEnabled: dataPendingEnabledVisibility.show });

    return (
        <>
            <ViewAllPropertiesDrawer
                row={leadRow}
                columns={columns}
                heading={resolveLeadDisplayName(leadRow)}
                loading={loading}
                error={error}
                notFound={notFound}
                notFoundMessage="Lead not found"
                customCellRenderers={leadEditableCellRenderers}
                valueGetterMap={LEADS_VALUE_GETTERS}
                modalActionConfig={modalActionConfig}
                onHistoryClick={handleOwnerHistoryClick}
                onClose={onClose}
            />
            <HistoryDrawer
                open={historyDrawerState.open}
                onClose={handleHistoryDrawerClose}
                referenceId={historyDrawerState.referenceId}
                fieldKey={historyDrawerState.fieldKey}
                title={historyDrawerState.title}
                companyName={historyDrawerState.companyName}
                valueLabelMap={historyDrawerState.valueLabelMap}
            />
        </>
    );
};

export default CompanyLeadPropertiesDrawer;
